import { PageBaseLayout } from 'components/layouts';
import { useEffect } from 'react';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { SettlementVatRatesFormFields } from 'types';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { settingsTexts } from 'consts/text';
import { SettlementVatRatesEditForm } from 'components/templates';
import { parseSettlementVatRatesSettingsDtoIntoSettlementVatRatesFormFields } from 'utils/parsers/settlementVatRates/parseSettlementVatRatesSettingsDtoIntoSettlementVatRatesFormFields';
import { useSettingsAbilities } from 'abilities';

export const VatRatesEditPage = () => {
    const { form, formInitialData, setFormData } = useEditForm<SettlementVatRatesFormFields>();

    const { displaySuccess } = useSimpleNotification();

    const { settingsAbilities } = useSettingsAbilities();
    const canManageVatRates = settingsAbilities.checkIfCanManageSettlementSettings();

    const {
        data: settlementVatRatesData,
        isError: isSettlementVatRatesError,
        isFetching: isSettlementVatRatesFetching,
    } = useAppQuery('SETTLEMENT_VAT_RATES', [], StaffHTTPService.settings.getSettlementVatRates, {
        retry: false,

        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (settlementVatRatesData) {
            setFormData(
                parseSettlementVatRatesSettingsDtoIntoSettlementVatRatesFormFields(
                    settlementVatRatesData,
                ),
            );
        }
    }, [setFormData, settlementVatRatesData]);

    const handleUpdateSettlementVatRatesSuccess = () =>
        displaySuccess(settingsTexts.settlements.vatRates.notifications.successfullyEdited);

    const {
        mutate: updateSettlementVatRates,
        error: updateSettlementVatRatesError,
        isLoading: isUpdateSettlementVatRatesLoading,
    } = useAppMutation(StaffHTTPService.settings.updateSettlementVatRates, {
        key: ['UPDATE_VAT_RATES'],
        onSuccess: handleUpdateSettlementVatRatesSuccess,
        invalidateQueryKeys: [['SETTLEMENT_VAT_RATES']],
    });

    const handleEditSettlementVatRatesFinish = (
        settlementVatRatesFormFields: SettlementVatRatesFormFields,
    ) =>
        updateSettlementVatRates({
            updateSettlementVatRatesSettingsDto: settlementVatRatesFormFields,
        });

    return (
        <PageBaseLayout isError={isSettlementVatRatesError}>
            <SettlementVatRatesEditForm
                form={form}
                initialValues={formInitialData}
                axiosError={updateSettlementVatRatesError}
                isLoading={isUpdateSettlementVatRatesLoading || isSettlementVatRatesFetching}
                isViewMode={!canManageVatRates}
                onFinish={handleEditSettlementVatRatesFinish}
            />
        </PageBaseLayout>
    );
};
